import { useWorkspaceText } from '../interface-copy';
import { useRef, useState, type ReactNode } from 'react';
import { useWorkspace } from '../context';
import { WorkspaceIcon } from '../icons';
import { agentsApi, type Agent } from './api';
import type { Act } from './shared';

/** Optional draft testing and explicit application for saved legacy drafts. */
export function DraftBar({
  agent,
  busy,
  act,
  showStatus = true,
  runAction,
}: {
  agent: Agent;
  busy: boolean;
  act: Act;
  /** False in the canvas footer, whose own sentence already states the draft's status. */
  showStatus?: boolean;
  runAction?: ReactNode;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const [publishing, setPublishing] = useState(false);
  const [note, setNote] = useState('');
  const tryButton = useRef<HTMLButtonElement>(null);
  if (!agent.draft) return runAction ?? null;
  const revision = agent.currentRevision;
  const mayApply = agent.canPublish;
  const publish = () => {
    if (!note.trim()) return;
    void act(
      async () => {
        await agentsApi.publish(agent.id, { revision, note: note.trim() });
        setPublishing(false);
        setNote('');
      },
      uiText('Applied version {revision}.', { revision }),
    );
  };
  const tryDraft = async () => {
    const ownsNavigation = ws.beginNavigation();
    await act(async () => {
      const started = await agentsApi.tryDraft(agent.id);
      if (ownsNavigation()) ws.openThread(started.threadId);
    }, uiText('The draft is running in a chat only you can read.'));
  };
  return (
    <div className="ws-agents-draftbar">
      {showStatus && (
        <span>
          {uiRich('{value1} · not published. Review the changes. Testing is optional.', {
            value1: <strong>{uiRich('Draft · revision {value1}', { value1: revision })}</strong>,
          })}
        </span>
      )}
      {!publishing && (
        <span className="ws-actions">
          {runAction}
          <button
            ref={tryButton}
            className={runAction ? 'ws-text-button' : 'ws-button small'}
            disabled={busy}
            onClick={() => void tryDraft()}
          >
            {uiRich('{value1} Test draft', { value1: <WorkspaceIcon name="play" size={12} /> })}
          </button>
          <button
            className="ws-button primary small"
            aria-disabled={busy || !mayApply}
            title={
              mayApply
                ? uiText('Apply this draft as a new version')
                : uiText('This draft cannot be applied.')
            }
            onClick={() => {
              if (busy || !mayApply) {
                tryButton.current?.focus();
                return;
              }
              setPublishing(true);
            }}
          >
            {uiText('Apply changes…')}
          </button>
        </span>
      )}
      {publishing && (
        <form
          className="ws-agents-publish"
          onSubmit={(event) => {
            event.preventDefault();
            publish();
          }}
        >
          <label>
            <span>{uiText('What changed?')}</span>
            <input
              type="text"
              value={note}
              maxLength={500}
              aria-label={uiText('What changed?')}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
          <span className="ws-actions">
            <button type="button" className="ws-button small" onClick={() => setPublishing(false)}>
              {uiText('Cancel')}
            </button>
            <button
              type="submit"
              className="ws-button primary small"
              disabled={busy || !note.trim()}
            >
              {uiRich('Apply version {value1}', { value1: revision })}
            </button>
          </span>
        </form>
      )}
    </div>
  );
}
